import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, MailCheck } from "lucide-react";
import Logo from "@/components/Logo";
import { toast } from "sonner";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-ink grain flex items-center justify-center px-4 py-16">
      <div className="absolute top-6 left-6">
        <Link to="/" className="flex items-center group">
          <Logo size={32} />
        </Link>
      </div>

      <div className="w-full max-w-md bg-surface border border-white/10 rounded-2xl p-8 sm:p-10 shadow-2xl shadow-terracotta/5" data-testid="forgot-card">
        {sent ? (
          <div className="text-center" data-testid="forgot-sent">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 text-emerald-400 flex items-center justify-center mx-auto mb-5">
              <MailCheck className="w-7 h-7" />
            </div>
            <h1 className="font-display text-3xl font-black mb-2">Check your inbox</h1>
            <p className="text-foreground/70 text-sm leading-relaxed">
              If an account exists for <strong className="text-foreground/90">{email}</strong>, we've just sent a password reset link there. The link is valid for 30 minutes.
            </p>
            <p className="text-foreground/50 text-xs mt-4">
              Didn't get it? Check spam, or wait a minute and try again.
            </p>
            <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-terracotta hover:text-ember mt-6" data-testid="link-back-to-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <h1 className="font-display text-3xl font-black mb-2">Forgot password?</h1>
              <p className="text-foreground/60 text-sm">Enter the email on your Cinevoya account and we'll send you a link to choose a new password.</p>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email" className="text-foreground/80 text-sm">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1.5 bg-ink border-white/10 h-11"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-ember text-white h-11" data-testid="forgot-submit-btn">
                {loading ? "Sending…" : "Send reset link"}
              </Button>
            </form>

            <p className="mt-6 text-sm text-center text-foreground/60">
              Remembered it?{" "}
              <Link to="/login" className="text-terracotta hover:text-ember" data-testid="link-back-to-login">
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
